import { MAX_QUEUE_SIZE, REQUEST_DELAYS } from '../data/constants';
import { fetchPropertyValuation } from '../../../services/api';

export const addToQueue = (requestQueue, listingId, address, city, state) => {
  // If queue is already at max size, don't add more
  if (requestQueue.length >= MAX_QUEUE_SIZE) {
    return requestQueue;
  }
  
  // Check if this listing is already in the queue
  if (requestQueue.some(item => item.listingId === listingId)) {
    return requestQueue;
  }

  return [...requestQueue, { listingId, address, city, state }];
};

export const processQueueItem = async (
  requestQueue,
  isProcessingQueue,
  failedRequests,
  fetchedPropertyData,
  setRequestQueue,
  setIsProcessingQueue,
  setFailedRequests,
  setFetchedPropertyData,
  processingTimeoutRef
) => {
  if (requestQueue.length === 0) {
    setIsProcessingQueue(false);
    return;
  }
  
  if (isProcessingQueue) return;
  
  setIsProcessingQueue(true);
  const { listingId, address, city, state } = requestQueue[0];

  // Skip if this request has already failed or been processed
  const currentStatus = fetchedPropertyData[listingId]?.status;
  if (failedRequests.has(listingId) || currentStatus === 'success' || currentStatus === 'no_data') {
    setRequestQueue(prev => prev.slice(1));
    setIsProcessingQueue(false);
    return;
  }

  try {
    // Update status to loading
    setFetchedPropertyData(prev => ({
      ...prev,
      [listingId]: {
        status: 'loading',
        queuePosition: 0,
        estimatedWaitTime: 0
      }
    }));
    
    // Make the API call
    const response = await fetchPropertyValuation(address, city, state);
    
    // Check if the response has useful data (either real or simulated)
    const hasUsefulData = response && response.estimatedValue && response.estimatedValue > 0;
    
    // Update state with the fetched data
    // Mark as success if we have any data (real or simulated), no_data if truly no data
    setFetchedPropertyData(prev => ({
      ...prev,
      [listingId]: {
        status: hasUsefulData ? 'success' : 'no_data',
        data: response,
        fetchedAt: new Date(),
        isSimulated: response?.source?.includes('simulated') || false,
        hasUsefulData
      }
    }));

    // Remove the processed request from queue
    setRequestQueue(prev => prev.slice(1));

    // Wait before processing next request
    processingTimeoutRef.current = setTimeout(() => {
      setIsProcessingQueue(false);
    }, REQUEST_DELAYS.NORMAL);

  } catch (error) {
    console.error('Error fetching property data:', error);
    const isRateLimit = error.response?.status === 429;
    
    if (isRateLimit) {
      // Wait longer and retry the same request
      processingTimeoutRef.current = setTimeout(() => {
        setIsProcessingQueue(false);
      }, REQUEST_DELAYS.RATE_LIMIT);
    } else {
      // Mark as error and add to failed requests
      setFailedRequests(prev => new Set(prev).add(listingId));
      setFetchedPropertyData(prev => ({
        ...prev,
        [listingId]: {
          status: 'error',
          error: error.message || 'Failed to fetch property data'
        }
      }));
      
      // Remove from queue and continue
      setRequestQueue(prev => prev.slice(1));
      processingTimeoutRef.current = setTimeout(() => {
        setIsProcessingQueue(false);
      }, REQUEST_DELAYS.NORMAL);
    }
  }
};

export const initializeQueue = (listings, fetchedPropertyData, failedRequests) => {
  const unfetchedListings = listings.filter(listing => {
    const listingId = listing.id || listing.property_id;
    const propertyData = fetchedPropertyData[listingId];
    const status = propertyData?.status;
    
    return (
      listing.address && // Must have an address
      !failedRequests.has(listingId) && // Not previously failed
      (!propertyData || status === 'error') && // Not fetched or errored
      status !== 'success' && // Not already successfully processed
      status !== 'no_data' && // Not already processed with no useful data
      status !== 'loading' && // Not currently being processed
      status !== 'queued' // Not already queued
    );
  });

  if (unfetchedListings.length > 0) {
    // Queue all unfetched listings at once
    const newQueue = unfetchedListings.map(listing => ({
      listingId: listing.id || listing.property_id,
      address: listing.address,
      city: listing.city,
      state: listing.state
    }));

    return {
      queue: newQueue,
      statusUpdates: newQueue.reduce((updates, item, index) => {
        updates[item.listingId] = {
          status: 'queued',
          queuePosition: index + 1,
          estimatedWaitTime: (index + 1) * 1.1
        };
        return updates;
      }, {})
    };
  }

  return { queue: [], statusUpdates: {} };
};